import React, { useRef, useState, useEffect } from "react";
import { Video, Mic, Sliders, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";

import {
  getHireTeamsOptions,
  submitHireTeamsCustomUnit,
  BulkCustomUnitProjectType,
  BulkCustomUnitBudgetRange,
  HireTeamsOptionItem,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import BrandDatePicker from "../../atoms/Utlis/BrandDatePicker";

import {
  PageWrap,
  HeroSection,
  HeroLeft,
  HeroBadge,
  HeroTitle,
  HeroDesc,
  HeroButtons,
  PrimaryBtn,
  SecondaryBtn,
  HeroRight,
  HeroImageCard,
  HeroImage,
  GlassOverlay,
  OverlayTitle,
  OverlaySubtitle,
  Section,
  SectionHeader,
  CenteredSectionHeader,
  SectionTitleBlock,
  SectionTitleBlockCentered,
  SectionTitle,
  SectionDesc,
  UnitsGrid,
  UnitCard,
  UnitCardTitle,
  UnitCardDesc,
  HighlightSection,
  AgenciesSection,
  HighlightContainer,
  CollageArea,
  CollageImgWrap,
  StatFloatingCard,
  HighlightContent,
  HighlightLabel,
  HighlightTitle,
  HighlightDesc,
  BulletList,
  BulletItem,
  CaseStudyBtn,
  AuthorCard,
  AgenciesGrid,
  AgencyCard,
  AgencyHeader,
  AgencyBgImage,
  AgencyLogo,
  AgencyBody,
  AgencyName,
  AgencyMeta,
  FormSection,
  FormCard,
  FormTitleBlock,
  FormTitle,
  FormSubtitle,
  FormRow,
  FormGroup,
  FormLabel,
  FormSelect,
  FormInput,
  FormTextarea,
  SubmitBtn,
  FormNote,
} from "./MarketplaceTeamPageStyles";

import teamsImg from "../../../assets/images/marketplace/teams.webp";
import team2Img from "../../../assets/images/marketplace/team2.webp";
import team3Img from "../../../assets/images/marketplace/team3.webp";
import team4Img from "../../../assets/images/marketplace/team4.webp";
import team5Img from "../../../assets/images/marketplace/team5.webp";

// ─── Static fallback options (spec-defined enums) ────────────────────────────

const FALLBACK_PROJECT_TYPES: HireTeamsOptionItem[] = [
  { value: "equipment",     label: "Equipment" },
  { value: "team",          label: "Team" },
  { value: "service",       label: "Service" },
  { value: "bulk_manpower", label: "Bulk Manpower" },
  { value: "other",         label: "Other" },
];

const FALLBACK_BUDGET_RANGES: HireTeamsOptionItem[] = [
  { value: "under_5k",  label: "Under $5k" },
  { value: "5k_15k",   label: "$5k – $15k" },
  { value: "15k_50k",  label: "$15k – $50k" },
  { value: "50k_plus", label: "$50k+" },
  { value: "flexible", label: "Flexible" },
];

const MIN_DESC_LENGTH = 20;

// ─── Component ────────────────────────────────────────────────────────────────

const MarketplaceTeamPage: React.FC = () => {
  const formRef = useRef<HTMLDivElement>(null);

  // ── Dropdown options (fetched, then fall back to static) ──────────────────
  const [projectTypeOptions, setProjectTypeOptions] = useState<HireTeamsOptionItem[]>(FALLBACK_PROJECT_TYPES);
  const [budgetRangeOptions, setBudgetRangeOptions]  = useState<HireTeamsOptionItem[]>(FALLBACK_BUDGET_RANGES);
  const [loadingOptions, setLoadingOptions]           = useState<boolean>(true);

  // ── Form state ────────────────────────────────────────────────────────────
  const [projectType, setProjectType] = useState<BulkCustomUnitProjectType>("equipment");
  const [otherProjectType, setOtherProjectType] = useState<string>("");
  const [budgetRange, setBudgetRange]  = useState<BulkCustomUnitBudgetRange>("5k_15k");
  const [details, setDetails]          = useState<string>("");
  const [startDate, setStartDate]      = useState<string>("");
  const [location, setLocation]        = useState<string>("");
  const [submitting, setSubmitting]    = useState<boolean>(false);

  // ── Validation errors ─────────────────────────────────────────────────────
  const [descError,       setDescError]       = useState<string>("");
  const [dateError,       setDateError]       = useState<string>("");
  const [locationError,   setLocationError]   = useState<string>("");
  const [otherTypeError, setOtherTypeError] = useState<string>("");

  // ── Fetch dynamic options on mount ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);
    getHireTeamsOptions()
      .then((opts) => {
        if (cancelled) return;
        if (opts.project_type?.length) {
          const allowed = ["equipment", "team", "service", "bulk_manpower", "other"];
          const validOptions = opts.project_type.filter((item) => allowed.includes(item.value));
          if (validOptions.length > 0) {
            setProjectTypeOptions(validOptions);
            setProjectType(validOptions[0].value as BulkCustomUnitProjectType);
          }
        }
        if (opts.budget_range?.length) {
          setBudgetRangeOptions(opts.budget_range);
          setBudgetRange(opts.budget_range[0].value as BulkCustomUnitBudgetRange);
        }
      })
      .catch(() => {
        // Silently fall back to static enums — already set as initial state
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => { cancelled = true; };
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ── Validate + submit ─────────────────────────────────────────────────────
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setDescError("");
    setDateError("");
    setLocationError("");
    setOtherTypeError("");

    // Validate
    let hasError = false;
    if (projectType === "other" && !otherProjectType.trim()) {
      setOtherTypeError("Please specify your custom project type.");
      hasError = true;
    }
    if (details.trim().length < MIN_DESC_LENGTH) {
      setDescError(`Please describe your project in at least ${MIN_DESC_LENGTH} characters (${details.trim().length}/${MIN_DESC_LENGTH}).`);
      hasError = true;
    }
    if (!startDate) {
      setDateError("Please select a start date.");
      hasError = true;
    }
    if (!location.trim()) {
      setLocationError("Please enter a location.");
      hasError = true;
    }
    if (hasError) return;

    // Auth gate — fire login modal if no token
    const token = localStorage.getItem("access_token");
    if (!token) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      toast.info("Please log in to submit a quote request.");
      return;
    }

    setSubmitting(true);
    try {
      const isOther = projectType === "other";
      const customType = otherProjectType.trim();

      const customFields = isOther && customType
        ? [{ key: "custom_project_type", label: "Other Project Type", value: customType }]
        : undefined;

      const extraFields = isOther && customType
        ? {
            custom_project_type: customType,
            other_project_type: customType,
          }
        : undefined;

      const createdReq: any = await submitHireTeamsCustomUnit({
        project_type: projectType,
        budget_range: budgetRange,
        description: details.trim(),
        date_from: startDate,
        location: location.trim(),
        ...(isOther && customType
          ? {
              title: `${customType} — ${location.trim() || "Remote"}`,
              other_project_type: customType,
              custom_project_type: customType,
              custom_fields: customFields,
              extra_fields: extraFields,
            }
          : {}),
      });

      const refStr = createdReq?.data?.reference || createdReq?.reference || "";
      toast.success(
        refStr
          ? `Quote request (${refStr}) submitted successfully! You can track received applications in your dashboard.`
          : "Quote request submitted successfully! You can track received applications in your dashboard."
      );

      // Reset form
      setDetails("");
      setStartDate("");
      setLocation("");
      setOtherProjectType("");
      setOtherTypeError("");
      setProjectType((projectTypeOptions[0]?.value as BulkCustomUnitProjectType) ?? "equipment");
      setBudgetRange((budgetRangeOptions[0]?.value as BulkCustomUnitBudgetRange) ?? "5k_15k");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to submit quote request. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Static unit cards data ────────────────────────────────────────────────
  const unitsData = [
    {
      id: "narrative",
      title: "The Narrative Unit",
      desc: "Director, DP, 1st AC, Gaffer, and Sound Mixer. Optimized for short films and narrative spots.",
      category: "Commercials",
      price: "From $4,500/day",
      image: team2Img,
      icon: <Video size={20} color="#FFC951" />,
      popular: false,
    },
    {
      id: "multicam",
      title: "The Multi-Cam Podcast",
      desc: "3-Camera setup, Tech Director, Audio Engineer, and Live Editor. Full studio or mobile rig available.",
      category: "Socials/Ad",
      price: "From $2,800/day",
      image: team3Img,
      icon: <Mic size={20} color="#FFC951" />,
      popular: true,
    },
    {
      id: "postprod",
      title: "Post-Production Cell",
      desc: "Lead Editor, Colorist, and Sound Designer working in a unified pipeline for rapid delivery.",
      category: "Documentaries",
      price: "Custom Pricing",
      image: team4Img,
      icon: <Sliders size={20} color="#FFC951" />,
      popular: false,
    },
  ];

  return (
    <PageWrap>
      {/* ── Hero ── */}
      <HeroSection>
        <HeroLeft>
          <HeroBadge>SCALE YOUR VISION</HeroBadge>
          <HeroTitle>
            Assemble Elite <br />
            <span className="gradient">Production Units</span> in Hours.
          </HeroTitle>
          <HeroDesc>
            Stop hunting for individuals. Hire pre-vetted, high-performance production
            teams and agencies ready to execute your most ambitious projects from day one.
          </HeroDesc>
          <HeroButtons>
            <PrimaryBtn onClick={scrollToForm}>Request a Custom Team Quote</PrimaryBtn>
            <SecondaryBtn onClick={scrollToForm}>View Agency Directory &rarr;</SecondaryBtn>
          </HeroButtons>
        </HeroLeft>

        <HeroRight>
          <HeroImageCard>
            <HeroImage src={teamsImg} alt="KwikMemories Elite Crew working" />
            <GlassOverlay>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#555", border: "1.5px solid #15181b" }} />
                  <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#777", border: "1.5px solid #15181b", marginLeft: "-8px" }} />
                  <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#999", border: "1.5px solid #15181b", marginLeft: "-8px" }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <OverlayTitle style={{ fontSize: "14px", fontWeight: 700, margin: 0, color: "var(--mp-headingColor, #171717)" }}>Active Unit: Yodda Y</OverlayTitle>
                  <OverlaySubtitle style={{ fontSize: "12px", margin: "2px 0 0", color: "var(--mp-featureDescColor, #434655)", opacity: 0.85, whiteSpace: "normal" }}>
                    Specializing in high-end commercial automotive cinematography.
                  </OverlaySubtitle>
                </div>
              </div>
            </GlassOverlay>
          </HeroImageCard>
        </HeroRight>
      </HeroSection>

      {/* ── Units Grid ── */}
      <Section>
        <SectionHeader style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "20px" }}>
          <SectionTitleBlock>
            <SectionTitle style={{ fontFamily: "Liberation Serif", fontSize: "36px", fontWeight: 700, margin: "0 0 8px" }}>
              Precision-Engineered Units
            </SectionTitle>
            <div style={{ color: "var(--mp-featureDescColor, #434655)", fontSize: "14px", lineHeight: "1.6", maxWidth: "600px" }}>
              Standardized team configurations for the most common production workflows. Ready to deploy on-site or remotely.
            </div>
          </SectionTitleBlock>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <button style={{ width: "36px", height: "36px", borderRadius: "50%", border: "1px solid var(--mp-border, rgba(23,23,23,0.12))", background: "var(--mp-cardBackground, #ffffff)", color: "var(--mp-headingColor, #171717)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ChevronLeft size={20} />
            </button>
            <button style={{ width: "36px", height: "36px", borderRadius: "50%", border: "1px solid var(--mp-border, rgba(23,23,23,0.12))", background: "var(--mp-cardBackground, #ffffff)", color: "var(--mp-headingColor, #171717)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ChevronRight size={20} />
            </button>
          </div>
        </SectionHeader>

        <UnitsGrid>
          {unitsData.map((unit) => (
            <UnitCard
              key={unit.id}
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "24px",
                minHeight: "320px",
                justifyContent: "space-between",
                border: unit.popular
                  ? "1.5px solid var(--mp-featureIconColor, #D863BA)"
                  : "1px solid var(--mp-border, rgba(23, 23, 23, 0.08))",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "var(--mp-bodyBackground, #f7f8fc)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--mp-border, rgba(23,23,23,0.12))" }}>
                  {unit.icon}
                </div>
                <UnitCardTitle style={{ fontSize: "20px", fontWeight: 700, margin: "8px 0 0" }}>{unit.title}</UnitCardTitle>
                <UnitCardDesc style={{ fontSize: "14px", lineHeight: "1.6", color: "var(--mp-featureDescColor, #434655)", margin: 0 }}>{unit.desc}</UnitCardDesc>
              </div>
              <div>
                <hr style={{ border: "none", borderTop: "1px solid var(--mp-border, rgba(23,23,23,0.08))", margin: "16px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className="amount" style={{ fontSize: "16px", fontWeight: 700, color: "var(--mp-headingColor, #171717)" }}>
                    {unit.price}
                  </span>
                  {unit.popular && (
                    <span style={{ fontSize: "10px", fontWeight: 700, padding: "4px 8px", borderRadius: "12px", background: "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)", color: "#402D00", textTransform: "uppercase" }}>
                      Popular
                    </span>
                  )}
                </div>
              </div>
            </UnitCard>
          ))}
        </UnitsGrid>
      </Section>

      {/* ── Highlight / Case Study ── */}
      <HighlightSection>
        <HighlightContainer>
          <CollageArea>
            <CollageImgWrap>
              <HeroImage src={team3Img} alt="Production setup" />
            </CollageImgWrap>
            <CollageImgWrap style={{ transform: "translateY(-30px)" }}>
              <HeroImage src={team2Img} alt="Behind the scenes shoot" />
            </CollageImgWrap>
            <CollageImgWrap>
              <HeroImage src={team4Img} alt="Editing room setup" />
            </CollageImgWrap>
            <CollageImgWrap style={{ transform: "translateY(-70px)", aspectRatio: "1 / 1.35" }}>
              <HeroImage src={team5Img} alt="Production crew briefing" />
            </CollageImgWrap>
            <StatFloatingCard>
              <div className="num">98%</div>
              <div className="txt">SUCCESS RATE</div>
            </StatFloatingCard>
          </CollageArea>

          <HighlightContent>
            <HighlightLabel>Case Study</HighlightLabel>
            <HighlightTitle>Global Campaign for &ldquo;Aura&rdquo; Smart Homes.</HighlightTitle>
            <HighlightDesc style={{ fontFamily: "Liberation Serif", fontWeight: 400, fontStyle: "italic", fontSize: "18px", lineHeight: "28px", color: "var(--mp-featureDescColor, #434655)" }}>
              &ldquo;We need to shoot in 4 countries simultaneously. Creatives provided 4 independent local teams that operated as a single unit. The consistency was flawless.&rdquo;
            </HighlightDesc>
            <AuthorCard>
              <div className="author-name">Marcus Thorne</div>
              <div className="author-title">Global Creative Director, Aura</div>
            </AuthorCard>
            <BulletList>
              {["22-person crew assembled in 72 hours", "Integrated real-time dailies pipeline"].map((text, i) => (
                <BulletItem key={i}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <defs>
                      <linearGradient id={`grad${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#FFC951" />
                        <stop offset="100%" stopColor="#C94DAA" />
                      </linearGradient>
                    </defs>
                    <circle cx="10" cy="10" r="9" stroke={`url(#grad${i})`} strokeWidth="1.5" fill="none" />
                    <path d="M6.5 10.5L8.5 12.5L13.5 7.5" stroke={`url(#grad${i})`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{text}</span>
                </BulletItem>
              ))}
            </BulletList>
            <CaseStudyBtn onClick={scrollToForm}>
              Read Full Story
              <span className="gradient-line" />
            </CaseStudyBtn>
          </HighlightContent>
        </HighlightContainer>
      </HighlightSection>

      {/* ── Elite Agencies ── */}
      <AgenciesSection>
        <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
          <CenteredSectionHeader style={{ borderBottom: "none", paddingBottom: 0, marginBottom: "36px" }}>
            <SectionTitleBlockCentered>
              <SectionTitle style={{ fontFamily: "Liberation Serif", fontSize: "36px", fontWeight: 700, margin: "0 0 12px" }}>
                Elite Agencies on the Platform
              </SectionTitle>
              <SectionDesc>Work with established houses that have built their reputations on excellence.</SectionDesc>
            </SectionTitleBlockCentered>
          </CenteredSectionHeader>

          <AgenciesGrid style={{ border: "none" }}>
            {[
              { name: "Vanguard Media",    loc: "NYC • Narrative Specialists", img: team2Img, initial: "V" },
              { name: "Lumen Collective",  loc: "London • Commercial Arts",    img: team3Img, initial: "L" },
              { name: "Skyward Digital",   loc: "LA • Aerial & Tech",          img: team4Img, initial: "S" },
              { name: "Mainstage Prod.",   loc: "Tokyo • Live Event Elite",    img: team5Img, initial: "M" },
            ].map((agency) => (
              <AgencyCard key={agency.name} style={{ border: "none" }}>
                <AgencyHeader>
                  <AgencyBgImage src={agency.img} alt={agency.name} />
                  <AgencyLogo>{agency.initial}</AgencyLogo>
                </AgencyHeader>
                <AgencyBody>
                  <AgencyName>{agency.name}</AgencyName>
                  <AgencyMeta>{agency.loc}</AgencyMeta>
                  <div style={{ display: "flex", gap: "2px", justifyContent: "center", margin: "8px 0" }}>
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <span key={idx} style={{ color: "#f59e0b", fontSize: "14px" }}>★</span>
                    ))}
                  </div>
                </AgencyBody>
              </AgencyCard>
            ))}
          </AgenciesGrid>
        </div>
      </AgenciesSection>

      {/* ── Build Your Custom Unit Form ── */}
      <FormSection ref={formRef}>
        <FormCard>
          <FormTitleBlock>
            <FormTitle>
              Build Your <span className="gradient">Custom Unit</span>
            </FormTitle>
            <FormSubtitle>
              Tell us what you're building. Our specialists will hand-curate a team that matches your specs and budget.
            </FormSubtitle>
          </FormTitleBlock>

          <form onSubmit={handleFormSubmit} noValidate>
            {/* Row 1: Project Type + Budget Range */}
            <FormRow>
              <FormGroup>
                <FormLabel htmlFor="hire-project-type">Project Type</FormLabel>
                <FormSelect
                  id="hire-project-type"
                  value={projectType}
                  onChange={(e) => {
                    const val = e.target.value as BulkCustomUnitProjectType;
                    setProjectType(val);
                    if (val !== "other") {
                      setOtherProjectType("");
                      setOtherTypeError("");
                    }
                  }}
                  disabled={loadingOptions}
                >
                  {projectTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="hire-budget-range">Budget Range</FormLabel>
                <FormSelect
                  id="hire-budget-range"
                  value={budgetRange}
                  onChange={(e) => setBudgetRange(e.target.value as BulkCustomUnitBudgetRange)}
                  disabled={loadingOptions}
                >
                  {budgetRangeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FormSelect>
              </FormGroup>
            </FormRow>

            {/* Custom Project Type (when "Other" is selected) */}
            {projectType === "other" && (
              <FormGroup $mb={24}>
                <FormLabel htmlFor="hire-other-project-type">
                  Specify Project Type
                  <span style={{ fontWeight: 400, opacity: 0.55, marginLeft: "8px", fontSize: "11px" }}>
                    (e.g., Drone Cinematography, Underwater Shoot, Virtual Production)
                  </span>
                </FormLabel>
                <FormInput
                  id="hire-other-project-type"
                  type="text"
                  placeholder="Enter your custom project type..."
                  value={otherProjectType}
                  onChange={(e) => {
                    setOtherProjectType(e.target.value);
                    if (otherTypeError && e.target.value.trim()) setOtherTypeError("");
                  }}
                  style={otherTypeError ? { borderColor: "#ef4444" } : undefined}
                />
                {otherTypeError && (
                  <span style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>
                    {otherTypeError}
                  </span>
                )}
              </FormGroup>
            )}

            {/* Description */}
            <FormGroup $mb={24}>
              <FormLabel htmlFor="hire-description">
                Project Scope &amp; Crew Requirements
                <span style={{ fontWeight: 400, opacity: 0.55, marginLeft: "8px", fontSize: "11px" }}>
                  (min {MIN_DESC_LENGTH} chars)
                </span>
              </FormLabel>
              <FormTextarea
                id="hire-description"
                placeholder="Describe the creative vision, required gear, and key team roles you need for this campaign."
                value={details}
                onChange={(e) => {
                  setDetails(e.target.value);
                  if (descError && e.target.value.trim().length >= MIN_DESC_LENGTH) setDescError("");
                }}
                required
                style={descError ? { borderColor: "#ef4444" } : undefined}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "4px" }}>
                {descError ? (
                  <span style={{ fontSize: "12px", color: "#ef4444", lineHeight: "1.4" }}>{descError}</span>
                ) : (
                  <span />
                )}
                <span style={{
                  fontSize: "11px",
                  color: details.trim().length < MIN_DESC_LENGTH ? "#ef4444" : "#10b981",
                  marginLeft: "auto",
                  flexShrink: 0,
                  opacity: details.length > 0 ? 1 : 0.5,
                  transition: "color 0.2s",
                }}>
                  {details.trim().length}/{MIN_DESC_LENGTH}
                </span>
              </div>
            </FormGroup>

            {/* Row 2: Start Date + Location */}
            <FormRow>
              <FormGroup>
                <FormLabel htmlFor="hire-start-date">Start Date</FormLabel>
                <BrandDatePicker
                  name="hire-start-date"
                  value={startDate}
                  required
                  onChange={(v) => {
                    setStartDate(v);
                    if (dateError) setDateError("");
                  }}
                />
                {dateError && (
                  <span style={{ fontSize: "12px", color: "#ef4444", marginTop: "2px" }}>{dateError}</span>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="hire-location">Location</FormLabel>
                <FormInput
                  id="hire-location"
                  type="text"
                  placeholder="City or Remote"
                  value={location}
                  onChange={(e) => {
                    setLocation(e.target.value);
                    if (locationError && e.target.value.trim()) setLocationError("");
                  }}
                  required
                  style={locationError ? { borderColor: "#ef4444" } : undefined}
                />
                {locationError && (
                  <span style={{ fontSize: "12px", color: "#ef4444", marginTop: "2px" }}>{locationError}</span>
                )}
              </FormGroup>
            </FormRow>

            {/* Submit */}
            <SubmitBtn
              id="hire-teams-submit"
              type="submit"
              disabled={submitting || loadingOptions}
              aria-busy={submitting}
            >
              {submitting ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <svg
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    style={{ animation: "spin 0.8s linear infinite" }}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="30 60" />
                  </svg>
                  Submitting…
                </span>
              ) : "Submit Quote Request"}
            </SubmitBtn>

            <FormNote>Response guaranteed within 4 business hours.</FormNote>
          </form>
        </FormCard>
      </FormSection>
    </PageWrap>
  );
};

export default MarketplaceTeamPage;
